import { useHistory } from "react-router";
import { FaWhatsapp } from "react-icons/fa";
import { formatPhoneNumber } from "../../utils/MascaraDeCampos";
import { IWhatsAppMensagens } from "./interface";
import { useColumnDateTimeSearch } from "../../hooks/FilterTable/date";
import { useGetColumnSearchPropsTypeString } from "../../hooks/FilterTable/objString";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, notification, Popconfirm, Tag, Tooltip } from "antd";
import { CheckOutlined, CheckSquareOutlined, CommentOutlined, QuestionCircleOutlined, UserOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import axios from "axios";
import WhatsAppMensagensService from "../../services/http/services/WhatsAppMensagens/WhatsAppMensagensService";
import './styles.css'
import { getMensagensWhatsAppAbertas } from "../../layouts/LayoutDashboard/requests/quantidadesMensagensWhatsApp";

export const useWhatsAppMensagens = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loadingModal, setLoadingModal] = useState(false);
    const [loadingTable, setLoadingTable] = useState(false);
    const [data, setData] = useState<IWhatsAppMensagens[]>([]);
    const [loadingButtonId, setLoadingButtonId] = useState<string | null>(null);
    const [selectedRow, setSelectedRow] = useState<IWhatsAppMensagens | null>(null);

    const refContagemDoFiltro = useRef<IWhatsAppMensagens[]>([]);

    const signal = axios.CancelToken.source();

    const history = useHistory();

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    const handleNavigation = useCallback((record: IWhatsAppMensagens) => {
        const params = new URLSearchParams();
        params.append("telefone", record.telefone.toString());
        params.append("listagem", "todos");

        history.push({
            pathname: "/usuarios",
            search: `?${params.toString()}`
        });
    }, [history]);

    const showModal = useCallback((record: IWhatsAppMensagens): void => {
        setLoadingModal(true);
        setSelectedRow(record);
        setIsModalOpen(true);
        setTimeout(() => {
            setLoadingModal(false);
        }, 500);
    }, []);

    const loadDataTable = useCallback(async () => {
        try {

            setLoadingTable(true);
            const data = await WhatsAppMensagensService.getMensagens(signal.token);
            setData(data);

        } catch (error) {

            let msgErro: any = (error as Error);
            if (msgErro?.message != 'Requisicao cancelada!') {
                setLoadingTable(false)
                notification.error({
                    message: 'Erro',
                    description: 'Não foi possivel buscar os dados!',
                });
            }

        } finally {
            setLoadingTable(false);
        }
    }, []);

    const updateStatusMensagem = useCallback(async (record: IWhatsAppMensagens) => {
        try {

            setLoadingButtonId(record.id);
            await WhatsAppMensagensService.putStatusMensagem(record.id, signal.token);
            notification.success({ message: 'Sucesso', description: 'Status atualizado!' });
            await loadDataTable();
            window.dispatchEvent(new Event('atualizarContagemBalon'));

            getMensagensWhatsAppAbertas();

        } catch (error) {

            let msgErro: any = (error as Error);
            if (msgErro?.message != 'Requisicao cancelada!') {
                setLoadingTable(false)
                notification.error({
                    message: 'Erro',
                    description: 'Não foi possivel atualizar o status!',
                });
            }

        } finally {
            setLoadingButtonId('');
        }

    }, []);

    const columns: ColumnsType<IWhatsAppMensagens> = [
        {
            title: "Nome",
            dataIndex: "nome",
            key: "nome",
            ellipsis: true,
            width: 150,
            ...useGetColumnSearchPropsTypeString('nome')
        },
        {
            title: "Telefone",
            dataIndex: "telefone",
            key: "telefone",
            width: 160,
            ...useGetColumnSearchPropsTypeString('telefone'),
            render: (telefone: number) => {
                const tel = telefone.toString();
                const telFormatted = formatPhoneNumber(tel, false);
                return (
                    <div className="divTel">
                        <span className="numberPhone">
                            {telFormatted}
                        </span>
                        <a
                            href={`https://api.whatsapp.com/send/?phone=${tel}&text&type=phone_number&app_absent=1`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="linkWhatsapp"
                        >
                            <FaWhatsapp />
                        </a>
                    </div>
                );
            },
        },
        {
            title: "Mensagem",
            dataIndex: "conteudo",
            key: "conteudo",
            ellipsis: true,
            ...useGetColumnSearchPropsTypeString('conteudo')
        },
        {
            title: "Recebida em",
            dataIndex: "recebida_em",
            key: "recebida_em",
            width: 170,
            ...useColumnDateTimeSearch('recebida_em'),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 110,
            filters: [
                { text: "Aberto", value: "Aberto" },
                { text: "Fechado", value: "Fechado" },
            ],
            onFilter: (value, record) => record.status === value,
            render: (status: string) => {
                const color = status === "Aberto" ? "blue" : status === "Fechado" ? "green" : "gray";
                return <Tag color={color}>{status.toUpperCase()}</Tag>;
            },
        },
        {
            title: "Ações",
            key: "acoes",
            width: 140,
            fixed: 'right',
            render: (record: IWhatsAppMensagens) => {
                return (
                    <div className="divAcao">
                        <Tooltip title="Ver mensagem" color="blue">
                            <Button
                                type="primary"
                                shape="circle"
                                icon={<CommentOutlined />}
                                onClick={() => showModal(record)}
                            />
                        </Tooltip>
                        <Tooltip title="Ir para usuário" color="#FFA500">
                            <Button
                                type="primary"
                                shape="circle"
                                icon={<UserOutlined />}
                                onClick={() => handleNavigation(record)}
                                style={{
                                    backgroundColor: '#FFA500',
                                    borderColor: '#FFA500',
                                }}
                            />
                        </Tooltip>
                        <Popconfirm
                            placement="left"
                            title="Marcar mensagem como respondida?"
                            icon={<QuestionCircleOutlined style={{ color: 'orange' }} />}
                            onConfirm={() => updateStatusMensagem(record)}
                            okText="Sim"
                            cancelText="Não"
                            disabled={record.status === 'Fechado'}
                            okButtonProps={{
                                style: {
                                    backgroundColor: '#008000',
                                    borderColor: '#008000',
                                    color: 'white',
                                    borderRadius: '4px'
                                },
                                type: 'primary'
                            }}
                            cancelButtonProps={{
                                danger: true,
                                style: { borderRadius: '4px' }
                            }}
                        >
                            <Tooltip
                                title={record.status === 'Fechado' ? 'Respondido' : 'Marcar como respondido'}
                                color={record.status === 'Fechado' ? '#a1a1a1' : '#008000'}
                            >
                                <Button
                                    disabled={record.status === 'Fechado'}
                                    type="primary"
                                    shape="circle"
                                    icon={
                                        record.status === 'Fechado' ? <CheckSquareOutlined /> : <CheckOutlined />
                                    }
                                    loading={loadingButtonId === record.id}
                                    style={{
                                        backgroundColor: record.status === 'Fechado' ? '#a1a1a1' : 'green',
                                        borderColor: record.status === 'Fechado' ? '#a1a1a1' : 'green',
                                    }}
                                />
                            </Tooltip>
                        </Popconfirm>
                    </div>
                )
            },
        },
    ];

    useEffect(() => {

        loadDataTable();

    }, []);

    useEffect(() => () => {
        signal.cancel("Requisicao cancelada!")
    }, []);

    return {
        data,
        columns,
        selectedRow,
        isModalOpen,
        loadingModal,
        loadingTable,
        refContagemDoFiltro,
        handleCancel,
    };
};
